/// <amd-module name="Repos/FilesHubCommon/Components/FilesEditableBreadcrumb" />
import * as React from "react";
import { IGitItem } from "Repos/Files/Contracts";
import * as Resources from "Repos/FilesHubCommon/Resources";
import { IReadonlyObservableArray, ObservableArray, ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import * as String from "VSS/Core/Util/String";
import { Fuzzaldrin, Scorer } from "VSS/Features/FuzzySearch";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { VssComponent } from "VSS/Platform/Layout";
import { Breadcrumb, IBreadcrumbItem } from "VSSUI/Breadcrumb";
import { DropdownExpandableTextField, IDropdownExpandableTextFieldProps } from "VSSUI/Dropdown";
import { EditableDropdown } from "VSSUI/EditableDropdown";
import { ExpandableTextField } from "VSSUI/ExpandableTextField";
import { Icon } from "VSSUI/Icon";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { IMenuItem } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { css } from "VSSUI/Util";
import { editableDropdownExtraItemClassName } from "VSSUI/Utilities/EditableDropdownItemProvider";
import { IFilesHubCommonService } from "../Contracts";

export interface IFilesEditableBreadcrumbProps {
    canSearch: boolean;
    editMode: ObservableValue<boolean>;
    filesService: IFilesHubCommonService;
    onPathSelected: (path: string) => void;
    selectedPath: string;
    screenSize: ScreenSize;
}

export class FilesEditableBreadcrumb extends VssComponent<IFilesEditableBreadcrumbProps> {
    private customSearching = false;
    private folderSearchItems: ObservableArray<IListBoxItem<IGitItem>>;
    private dropdownCollection: ObservableCollection<IListBoxItem<IGitItem>>;
    private dropdownKey = 0;
    private dropdownRef = React.createRef<EditableDropdown<IGitItem>>();

    public render() {
        const { canSearch, filesService, onPathSelected, selectedPath } = this.props;
        const rootItem = filesService.getRootItem();

        const breadcrumbItems: IBreadcrumbItem[] = [];
        const parts = selectedPath.split("/");
        let path = "/";

        for (const part of parts) {
            if (part || path === "/") {
                if (path !== "/") {
                    path += "/";
                }
                path += part;

                const ariaLabel = part ? undefined : String.format(Resources.RepositoryRoot, rootItem.name);
                breadcrumbItems.push({
                    ariaLabel,
                    iconProps: part ? undefined : { iconName: "FabricFolder" },
                    key: path,
                    onClick: path === selectedPath ? this.onBreadcrumbContainerClicked : this.getBreadcrumbClickHandler(part ? path : rootItem.path),
                    text: part,
                    menuItemProps: part ? undefined : ({ iconProps: undefined, text: ariaLabel } as IMenuItem)
                });
            }
        }

        return (
            <Observer editMode={this.props.editMode}>
                {(props: { editMode: boolean }) => {
                    this.customSearching = false;
                    return props.editMode || (selectedPath === "/" && this.props.screenSize > ScreenSize.xsmall) ? (
                        <EditableDropdown
                            allowFreeform={true}
                            autoAccept={false}
                            className="repos-files-header-dropdown flex-grow"
                            items={this.getPathDropdownItems()}
                            key={++this.dropdownKey} // Reset all state when flipping in and out of edit mode
                            ref={this.dropdownRef}
                            onCollapse={() => {
                                if (this.props.editMode.value) {
                                    this.props.editMode.value = false;
                                }
                            }}
                            filterThrottleWait={250}
                            filterItems={this.filterItems}
                            onValueChange={value => {
                                if (value) {
                                    onPathSelected(value.data ? value.data.path : value.text || "");
                                    this.props.editMode.value = false;
                                }
                            }}
                            placeholder={canSearch ? Resources.FileInputPlaceholder : Resources.FileInputPlaceholderNoSearch}
                            renderExpandable={props => (
                                <FilePathDropdownTextBox
                                    {...props}
                                    initialFocus={this.props.editMode.value}
                                    onUserEdit={this.ensureCustomSearchResults}
                                />
                            )}
                            text={selectedPath.substr(1)}
                        />
                    ) : (
                        <div
                            className="repos-files-header-breadcrumb-container flex-grow flex-row scroll-hidden"
                            onClick={this.onBreadcrumbContainerClicked}
                        >
                            <Breadcrumb className="repos-files-header-breadcrumb flex-grow" key={selectedPath} items={breadcrumbItems} />
                        </div>
                    );
                }}
            </Observer>
        );
    }

    public componentDidMount() {
        super.componentDidMount();

        const shortcutService = this.context.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
        shortcutService.registerShortcut(Resources.FilesShortcutGroup, {
            combos: ["t"],
            description: Resources.PathShortcutDescription,
            action: () => {
                if (this.dropdownRef.current) {
                    this.dropdownRef.current.focus();
                } else {
                    this.props.editMode.value = true;
                }
            }
        });
    }

    public componentWillUnmount() {
        super.componentWillUnmount();

        const shortcutService = this.context.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
        shortcutService.unRegisterShortcut(Resources.FilesShortcutGroup, "t");
    }

    private getBreadcrumbClickHandler(path: string) {
        return (event: React.MouseEvent<HTMLElement>) => {
            this.props.filesService.setSelectedPath(path);
            event.preventDefault();
        };
    }

    private getPathDropdownItems(): IReadonlyObservableArray<IListBoxItem<IGitItem>> {
        const collection = new ObservableCollection<IListBoxItem<IGitItem>>();

        if (this.props.canSearch) {
            this.customSearching = false;
            this.folderSearchItems = new ObservableArray();

            const selectedItem = this.props.filesService.getSelectedItem().value;
            if (selectedItem.isFolder) {
                const childItems = this.props.filesService.getChildItems(selectedItem.path);
                const processChildItems = () => {
                    if (childItems.ready) {
                        this.folderSearchItems.push(...(childItems.value.map(this.gitItemToListBoxItem).filter(Boolean) as IListBoxItem<IGitItem>[]));
                        childItems.ready.unsubscribe(processChildItems);
                    }
                };

                if (childItems.ready.value) {
                    processChildItems();
                } else {
                    childItems.ready.subscribe(processChildItems);
                }
            } else {
                const listBoxItem = this.gitItemToListBoxItem(selectedItem);
                if (listBoxItem) {
                    this.folderSearchItems.push(listBoxItem);
                }
            }

            this.folderSearchItems.push({
                className: "repos-files-header-search-description",
                id: "search-description",
                text: Resources.HeaderSearchDescription,
                type: ListBoxItemType.Header
            });
            collection.push(this.folderSearchItems);
        }

        this.dropdownCollection = collection;

        return this.dropdownCollection;
    }

    private ensureCustomSearchResults = (): void => {
        if (!this.customSearching) {
            this.customSearching = true;

            if (this.folderSearchItems) {
                // Forget local search items to avoid duplicates with allFiles.
                this.folderSearchItems.value = [];
            }

            this.dropdownCollection.push(this.props.filesService.getAllFiles(), this.gitItemToListBoxItem);
        }
    };

    private gitItemToListBoxItem = (item: IGitItem): IListBoxItem<IGitItem> | undefined => {
        const path = item.path.substr(1) || "/";
        if (!item.isInvalid) {
            return {
                data: item,
                iconProps: item.iconProps,
                id: path,
                text: path
            };
        } else {
            return undefined;
        }
    };

    private filterItems = (filterText: string, items: IListBoxItem<IGitItem>[]) => {
        // Ignore spaces, underscores, colons, dashes and remove leading / and \
        const trimmedText = filterText.replace(/(^[\/]+)|([ _\-:])/g, "").replace(/\\/g, "/");
        const trimmedTextLower = trimmedText.toLowerCase();
        const trimmedTextUpper = trimmedText.toUpperCase();

        let filteredItems: IListBoxItem<IGitItem>[] = [];
        const filterMatches: number[][] = [];
        const scoredCandidates: { item: IListBoxItem<IGitItem>; index: number; score: number }[] = [];

        for (let i = 0, l = items.length; i < l; i++) {
            const item = items[i];
            // The filler (use "xxx" item will have no data property, always include that item if present)
            if (!item.data || (item.text && Scorer.isMatch(item.text, trimmedTextLower, trimmedTextUpper))) {
                if (item.className === editableDropdownExtraItemClassName) {
                    scoredCandidates.push({ item: item, index: i, score: 0 });
                    continue;
                }
                if (item.text) {
                    const score = Scorer.score(item.text, trimmedText, undefined, true, true, Scorer.defaultPathSeparator);
                    scoredCandidates.push({ item: item, index: i, score: score });
                }
            }
        }

        scoredCandidates.sort((item1, item2) => {
            return item2.score - item1.score;
        });

        filteredItems = scoredCandidates.map(scoredCandidates => {
            if (scoredCandidates.item.className === editableDropdownExtraItemClassName) {
                filterMatches.push([]);
            } else {
                filterMatches.push(Fuzzaldrin.match(scoredCandidates.item.text, filterText));
            }
            return scoredCandidates.item;
        });

        const filteredIndexMap = scoredCandidates.map(c => c.index);

        return { filteredItems, filteredIndexMap, filterMatches };
    };

    private onBreadcrumbContainerClicked = (event: React.MouseEvent<HTMLElement>) => {
        if (!event.defaultPrevented) {
            this.props.editMode.value = true;
        }
    };
}

interface IFilePathDropdownTextBoxProps extends IDropdownExpandableTextFieldProps {
    initialFocus: boolean;
    onUserEdit: () => void;
}

class FilePathDropdownTextBox extends React.Component<IFilePathDropdownTextBoxProps> {
    public render() {
        return (
            <DropdownExpandableTextField
                {...this.props}
                prefixIconProps={{
                    render: className => (
                        <div className={css(className, "flex-row flex-center")} onClick={this.focusTextField}>
                            <Icon className="bolt-breadcrumb-item-icon icon-only cursor-pointer" iconName="FabricFolder" />
                            <div className="bolt-breadcrumb-divider">
                                <div className="bolt-breadcrumb-forward-slash">/</div>
                            </div>
                        </div>
                    )
                }}
                hideDropdownIcon={true}
                onChange={this.onChange}
            />
        );
    }

    public componentDidMount() {
        if (this.props.initialFocus) {
            this.focusTextField();
        }
    }

    private focusTextField = (): void => {
        const expandableTextField = this.props.expandableRef.current as ExpandableTextField;
        if (expandableTextField) {
            expandableTextField.select();
            expandableTextField.focus();
        }
    };

    private onChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string): void => {
        if (this.props.onChange) {
            this.props.onChange(event, value);
        }
        this.props.onUserEdit();
    };
}
